import React from 'react';
import PropTypes from 'prop-types';
import { FaEdit, FaHotel, FaMap, FaTrashAlt } from 'react-icons/fa';
import { formatBookingDate, formatNumber, formatNumberCurrency } from 'helpers/utils';
import GoogleMapsLink from 'helpers/googleMapsLink';
import { Card, ActionDropdown } from 'components/common';
import BookingAttachment from '../../BookingAttachment';
import HotelUploadAttachmentDirectly from './HotelUploadAttachmentDirectly';

const Field = ({ label, children }) => (
  <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-0.5 sm:tw-gap-2 tw-mb-1.5 tw-text-sm">
    <span className="sm:tw-w-40 sm:tw-flex-shrink-0 tw-font-semibold tw-text-slate-600">{label}</span>
    <span className="tw-text-blue-700 tw-min-w-0 tw-break-words">{children}</span>
  </div>
);
Field.propTypes = { label: PropTypes.node, children: PropTypes.node };

const HotelItem = ({ booking, hotel, editHotelBooking, deleteHotelBooking, handleRemoveAttachment }) => {
  const items = [
    { label: 'Edit', icon: <FaEdit />, onClick: editHotelBooking },
    { divider: true },
    { label: 'Delete', icon: <FaTrashAlt />, danger: true, onClick: deleteHotelBooking },
  ];

  return (
    <Card
      padding="none"
      className="tw-mb-4"
      header={(
        <div className="tw-flex tw-items-start tw-justify-between">
          <div>
            <div className="tw-text-base tw-font-semibold tw-uppercase tw-text-blue-700 tw-flex tw-items-center tw-gap-2"><FaHotel /> {hotel.name} (ID#{hotel.id})</div>
            <div className="tw-text-xs tw-mt-1 tw-flex tw-items-center tw-gap-1"><FaMap /> <GoogleMapsLink address={hotel.address} /></div>
          </div>
          <ActionDropdown items={items} />
        </div>
      )}
    >
      <div className="tw-p-4">
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6">
          <div>
            <h6 className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500 tw-mb-3">Hotel Information</h6>
            <Field label="Hotel name">{hotel.name}</Field>
            <Field label="Check in">{formatBookingDate(hotel.check_in_time)}</Field>
            <Field label="Check out">{formatBookingDate(hotel.check_out_time)}</Field>
            <Field label="Tel"><a href={`tel:${hotel.tel}`}>{hotel.tel}</a></Field>
            <Field label="Address"><GoogleMapsLink address={hotel.address} /></Field>
            <Field label="Created date">{hotel.created_date}</Field>
            <Field label="Created by">{hotel.created_by}</Field>
          </div>
          <div>
            <h6 className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500 tw-mb-3">Expense</h6>
            <Field label="Amount currency">{formatNumberCurrency(hotel.cost_currency, hotel.currency)}</Field>
            {hotel && hotel.currency === 'USD' && <Field label="Exchange rate">{formatNumber(hotel.exchange_rate)} VND</Field>}
            <Field label="Amount">{formatNumberCurrency(hotel.cost, 'VND')}</Field>
            <Field label="Note"><span dangerouslySetInnerHTML={{ __html: hotel.notes }} /></Field>
          </div>
        </div>

        <div className="tw-mt-4">
          <h6 className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500 tw-mb-3">Attachments</h6>
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
            <HotelUploadAttachmentDirectly booking={booking} hotel={hotel} />
            <div className="tw-flex tw-flex-wrap tw-gap-1">
              {(hotel.documents || []).map((attachment) => (
                <BookingAttachment
                  {...attachment}
                  key={attachment.id}
                  handleRemoveAttachment={(documentId) => handleRemoveAttachment(hotel.id, documentId)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

HotelItem.propTypes = {
  booking: PropTypes.object.isRequired,
  hotel: PropTypes.object.isRequired,
  editHotelBooking: PropTypes.func.isRequired,
  deleteHotelBooking: PropTypes.func.isRequired,
  handleRemoveAttachment: PropTypes.func.isRequired,
};

export default HotelItem;
